// src/utils/dateUtils.js

/**
 * Parse ISO date string and extract date/time components
 * without timezone conversion. Backend sends timestamps in business timezone.
 * @param {string} dateString - ISO date string (e.g., "2025-11-08T02:38:00+05:30")
 * @returns {object} Object with year, month, day, hours, minutes, seconds
 */
const parseISOString = (dateString) => {
  // Extract date and time parts from ISO string
  // Format: YYYY-MM-DDTHH:mm:ss+TZ or YYYY-MM-DDTHH:mm:ss.sss+TZ
  let match = dateString.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
  
  if (!match) {
    match = dateString.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (!match) {
      throw new Error('Invalid date string format');
    }
  }
  
  return {
    year: parseInt(match[1], 10),
    month: parseInt(match[2], 10),
    day: parseInt(match[3], 10),
    hours: parseInt(match[4], 10),
    minutes: parseInt(match[5], 10),
    seconds: parseInt(match[6], 10)
  };
};

/**
 * Format a date string without timezone conversion
 * The backend already sends timestamps converted to business timezone
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return '';
  
  if (typeof dateString === 'string') {
    const parts = parseISOString(dateString);
    const m = String(parts.month).padStart(2, '0');
    const d = String(parts.day).padStart(2, '0');
    const y = parts.year;
    
    // Return in format: MM/DD/YYYY (adjust based on locale preference)
    return `${d}/${m}/${y}`;
  }
  
  // Fallback for Date objects
  const date = dateString;
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  
  return `${d}/${m}/${y}`;
};

/**
 * Format time without timezone conversion
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {object} options - Formatting options
 * @returns {string} Formatted time string
 */
export const formatTime = (dateString, options = {}) => {
  if (!dateString) return '';
  
  const {
    hour12 = true
  } = options;
  
  if (typeof dateString === 'string') {
    const parts = parseISOString(dateString);
    const hours = parts.hours;
    const minutes = String(parts.minutes).padStart(2, '0');
    const seconds = String(parts.seconds).padStart(2, '0');
    
    if (hour12) {
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes}:${seconds} ${period}`;
    }
    
    return `${String(hours).padStart(2, '0')}:${minutes}:${seconds}`;
  }
  
  // Fallback for Date objects
  const date = dateString;
  const hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  
  if (hour12) {
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes}:${seconds} ${period}`;
  }
  
  return `${String(hours).padStart(2, '0')}:${minutes}:${seconds}`;
};

/**
 * Format date and time without timezone conversion
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {object} options - Formatting options
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (dateString, options = {}) => {
  if (!dateString) return '';
  
  const dateStr = formatDate(dateString, options);
  const timeStr = formatTime(dateString, options);
  
  return `${dateStr} ${timeStr}`;
};

/**
 * Format date in a shorter format (without year if current year)
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDateShort = (dateString) => {
  if (!dateString) return '';
  
  if (typeof dateString === 'string') {
    const parts = parseISOString(dateString);
    const now = new Date();
    const currentYear = now.getFullYear();
    
    const month = String(parts.month).padStart(2, '0');
    const day = String(parts.day).padStart(2, '0');
    
    // If same year, don't show year
    if (parts.year === currentYear) {
      return `${month}/${day}`;
    }
    
    return `${month}/${day}/${parts.year}`;
  }
  
  // Fallback for Date objects
  const date = dateString;
  const now = new Date();
  
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const year = date.getUTCFullYear();
  
  if (year === now.getUTCFullYear()) {
    return `${month}/${day}`;
  }
  
  return `${month}/${day}/${year}`;
};

/**
 * Format time in shorter format (without seconds)
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {object} options - Formatting options
 * @returns {string} Formatted time string
 */
export const formatTimeShort = (dateString, options = {}) => {
  if (!dateString) return '';
  
  const {
    hour12 = true
  } = options;
  
  if (typeof dateString === 'string') {
    const parts = parseISOString(dateString);
    const hours = parts.hours;
    const minutes = String(parts.minutes).padStart(2, '0');
    
    if (hour12) {
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes} ${period}`;
    }
    
    return `${String(hours).padStart(2, '0')}:${minutes}`;
  }
  
  // Fallback for Date objects
  const date = dateString;
  const hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  
  if (hour12) {
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes} ${period}`;
  }
  
  return `${String(hours).padStart(2, '0')}:${minutes}`;
};
/**
 * Today's date as YYYY-MM-DD in the browser's local time (toISOString() would give the UTC date)
 * @returns {string}
 */
export const getLocalTodayYMD = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/**
 * Clamp a YYYY-MM-DD value so it is never later than today (empty values pass through)
 * @param {string} value
 * @returns {string}
 */
export const clampToToday = (value) => {
  const today = getLocalTodayYMD();
  return value && value > today ? today : value;
};
